import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { Dialog, DialogTitle, DialogContent, DialogContentText } from '@mui/material';
import useDisplayMessage from 'components/messages/useMessage';
import PhoneNumberInputField from 'components/util/PhoneNumberInputField';
import AwaitLoading from 'components/util/AwaitLoading';
import { useTranslator } from 'translation/useTranslator';
import { Button, TextField } from '@nps.today/component-library';

export interface MfaRequiredDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  userPhoneNumber: string;
  updatePhoneNumber: (phoneNumber: string, authCode?: string) => Promise<any>;
  updateMultiFactorEnabled: (enable: boolean, acceptedPhoneNumber: string) => Promise<any>;
}

const MfaRequiredDialog = (props: MfaRequiredDialogProps) => {
  const translator = useTranslator();
  const { open, setOpen, userPhoneNumber, updatePhoneNumber, updateMultiFactorEnabled } = props;
  const [confirmedPhoneNumber, setConfirmedPhoneNumber] = useState<string>('');
  const [smsCodeSent, setSmsCodeSent] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const displayMessage = useDisplayMessage();

  const handleClose = (event: any, reason: string) => {
    if (reason && reason === 'backdropClick') {
      return;
    }
    setOpen(false);
  };

  useEffect(() => {
    if (userPhoneNumber) {
      setConfirmedPhoneNumber(userPhoneNumber);
    }
  }, [userPhoneNumber]);

  const handleSendSmsCode = async (phoneNumber: string) => {
    if (!phoneNumber) {
      displayMessage.error({
        message: {
          title: translator.invalidPhoneNumber()
        },
        autoHideDuration: 5000
      });
      return;
    }

    setLoading(true);
    try {
      await updatePhoneNumber(phoneNumber);
      setSmsCodeSent(true);
      displayMessage.success({
        message: {
          title: translator.smsCodeSentSuccessfully()
        },
        autoHideDuration: 5000
      });
    } catch (err) {
      console.error(err);
      displayMessage.error({
        message: {
          title: translator.smsCodeFailedToSend()
        },
        autoHideDuration: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifySmsCode = async (values: { phoneNumber: string; smsCode: string }) => {
    if (!values.phoneNumber) {
      displayMessage.error({
        message: {
          title: translator.invalidPhoneNumber()
        },
        autoHideDuration: 5000
      });
      return;
    }

    if (!values.smsCode) {
      displayMessage.error({
        message: {
          title: translator.fieldRequiredError(translator.smsCode())
        },
        autoHideDuration: 5000
      });
      return;
    }

    setLoading(true);
    try {
      await updatePhoneNumber(values.phoneNumber, values.smsCode);
      await updateMultiFactorEnabled(true, values.phoneNumber);
      setOpen(false);
      displayMessage.success({
        message: {
          title: translator.mfaActivatedSuccessfully()
        },
        autoHideDuration: 5000
      });
    } catch (error: any) {
      console.error(error);
      displayMessage.error({
        message: {
          title: translator.mfaActivationFailed()
        },
        autoHideDuration: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      phoneNumber: confirmedPhoneNumber,
      smsCode: ''
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      await handleVerifySmsCode(values);
    }
  });

  return (
    <Dialog onClose={handleClose} open={open}>
      <DialogTitle>{translator.mfaRequired()}</DialogTitle>
      <DialogContent>
        <AwaitLoading isLoading={loading} />
        <form onSubmit={formik.handleSubmit}>
          <DialogContentText>{translator.mfaDialogPreCodeSendDescription()}</DialogContentText>
          <PhoneNumberInputField
            required={true}
            dataTestId="mfa-phone-2"
            fieldName="phoneNumber"
            label={translator.phoneNumber()}
            formik={formik}
            initialValue={confirmedPhoneNumber}
          />
          <Button
            dataTestId="mfa-send-sms-code"
            onClick={() => handleSendSmsCode(formik.values.phoneNumber)}
          >
            {translator.sendSmsCode()}
          </Button>
          {smsCodeSent && (
            <>
              <DialogContentText>{translator.mfaDialogPostCodeSendDescription()}</DialogContentText>
              <TextField
                dataTestId="mfa-sms-code"
                autoFocus
                required
                margin="dense"
                id="mfa-smsCode"
                name="smsCode"
                label={translator.smsCode()}
                type="text"
                fullWidth
                variant="standard"
                onChange={formik.handleChange}
                value={formik.values.smsCode}
              />
              <Button dataTestId="mfa-verify-sms-code" type="submit">
                {translator.submitAndActivateMfa()}
              </Button>
            </>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default MfaRequiredDialog;
